import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import Icon from '@components/custom/Icon';
import ModuleHeader from '@components/ModuleHeader';
import Spinner from '@components/custom/Spinner';
import Table from '@components/custom/Table';

import '@assets/css/wallets.css';

import { crudActions } from '@sm/services/crudActions';
import { showAlert } from '@sm/store/alertSlice';
import { useCheckPageAccess } from '@sm/hooks/useCheckPageAccess';

const Wallets = () => {
  const dispatch = useDispatch();
  const selectedCryptoWalletType = 'external';

  const [isCryptoLoading, setIsCryptoLoading] = useState(true);
  const [cryptoWallets, setCryptoWallets] = useState([]);

  const cryptoColumns = useMemo(() => [
    {
      value: 'currency',
      label: 'Currency',
      leftAligned: true,
      isNumber: false
    }, {
      value: 'blockchain',
      label: 'Blockchain',
      leftAligned: true,
      isNumber: false
    }, {
      value: 'name',
      label: 'Name',
      leftAligned: true,
      isNumber: false
    }, ...(selectedCryptoWalletType === 'external' ? [{
      value: 'primary',
      label: 'Primary',
      centerAligned: true,
      isNumber: false
    }] : []), {
      value: 'wallet',
      label: 'Wallet Address',
      leftAligned: true,
      isNumber: false
    }, {
      value: 'status',
      label: 'Status',
      rightAligned: true,
      isNumber: false
    }
  ], [selectedCryptoWalletType]);

  const getWalletsList = useCallback((isCrypto, walletType) => {
    setIsCryptoLoading(true);
    
    crudActions.get(`v1/wallets?fiat=${!isCrypto}`)
      .then(response => {
        const wallets = walletType === 'external' ? response?.wallets : [];
        setCryptoWallets(wallets);
      })
      .catch(err => {
        dispatch(showAlert('danger', err?.message));
      })
      .finally(() => {
        setIsCryptoLoading(false);
      });
  }, [dispatch]);
  
  useEffect(() => {
    getWalletsList(true, selectedCryptoWalletType);
  }, [getWalletsList, selectedCryptoWalletType]);

  const handlePrimaryIconClick = (walletId) => {
    if (!walletId) return;

    crudActions.put(`v1/wallets/primary/${walletId}`)
      .then(response => {
        setCryptoWallets(response);
      })
      .catch(err => {
        dispatch(showAlert('danger', err?.message));
      });
  };

  const prepareTableData = (arr) => {
    return arr.map((elem, index) => {
      const walletAddress = elem.fields.find(field => field.name === 'wallet')?.value;
      return {
        ...elem,
        name: elem.fields.find(field => field.name === 'name')?.value || 'My Crypto Wallet ' + index,
        currency: elem.currency.split('_')[0],
        blockchain: elem.currency.split('_')[1],
        primary: (
          <Icon
            name='star'
            size={ 20 }
            disabled={ elem.status !== 'Approved' }
            color={ elem.primary ? '#cd8533' : '#2d2d2d' }
            onClick={ () => handlePrimaryIconClick(elem.id) }
          />
        ),
        wallet: walletAddress,
        status: (
          <span className={ `status ${elem.status === 'Approved' ? 'green' : (elem.status === 'Pending' ? 'orange' : 'grey')}` }> { elem.status } </span>
        )
      };
    });
  };

  const isAbletoEdit = useCheckPageAccess("EDIT_WALLET");

  return (
    <>
      <div className='col-12 module settlement wallets'>
        <div className='component shadow mb-4 position-relative'>
          <ModuleHeader
            id='account_balance_wallet'
            title='Crypto Wallets'
            actions={
              <>
                {isAbletoEdit && (
                  <NavLink to='/create-wallet' className='btn-link' state={ { type: 'crypto' } }>
                    Create Wallet
                  </NavLink>
                )}
              </>
            }
          />

          {isCryptoLoading && <Spinner /> }

          <Table
            className='mt-3 overflow-table crypto'
            columns={ cryptoColumns }
            data={ prepareTableData(cryptoWallets) }
            disableSort={ true }
          />

        </div>
      </div>
    </>
  );
};

export default Wallets;